import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  dominant,
  FA_COLOR,
  FA_ORDER,
  vectorAt,
  type EmotionKey,
} from '@/components/researcher/liveDemo/constants'

/**
 * Respondent-facing "Twoje wyniki" side panel: shows the emotion distribution at
 * the current playhead position of an emotion-timeline playback, with the dominant
 * emotion prominently highlighted. Pure / presentational — no fetching, no timers.
 *
 * Public respondent flow: copy is hardcoded Polish (no i18n here by design).
 */

type Props = {
  /** Per-emotion values 0..1 sampled on `gridMs`; `null` where no face was analysed. */
  series: Record<EmotionKey, (number | null)[]>
  /** Grid timestamps in ms, ascending. */
  gridMs: number[]
  /** Current playhead position in ms. */
  currentMs: number
}

/** Polish display labels for each expression. */
export const PL_LABEL: Record<EmotionKey, string> = {
  happy: 'Radość',
  surprised: 'Zaskoczenie',
  neutral: 'Neutralność',
  sad: 'Smutek',
  angry: 'Złość',
  disgusted: 'Wstręt',
  fearful: 'Strach',
}

export default function EmotionSummaryPanel({ series, gridMs, currentMs }: Props) {
  const theme = useTheme()
  const c = theme.affexy.colors

  const raw = vectorAt(series, gridMs, currentMs)
  // `vectorAt` reads a null (no analysed face) grid point as 0, so an all-zero
  // vector means the playhead sits in a dropout — show "no data", not "0%".
  const vec = raw && FA_ORDER.some((k) => (raw[k] ?? 0) > 0) ? raw : null
  const active = vec !== null
  const dom = active ? dominant(vec) : null
  const domKey = dom ? dom[0] : null
  const domColor = domKey ? FA_COLOR[domKey] : null
  const on = !!(dom && active)

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid rgba(16,24,40,.08)',
        borderRadius: 3,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Panel header */}
      <Box>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: c.ink56,
          }}
        >
          Rozkład emocji
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: c.ink56, mt: 0.25 }}>
          W bieżącym momencie odtwarzania
        </Typography>
      </Box>

      {/* Dominant emotion card */}
      <Box
        sx={{
          background:
            on && domColor ? `linear-gradient(135deg, ${domColor}dd, ${domColor})` : c.gray100,
          borderRadius: '18px',
          p: 2.5,
          color: on ? '#fff' : c.ink56,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minHeight: 120,
          transition: 'background 240ms cubic-bezier(.2,0,0,1)',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            fontWeight: 700,
            opacity: 0.7,
          }}
        >
          Dominująca emocja
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.75 }}>
          <Typography
            sx={{
              fontFamily: '"Roboto"',
              fontWeight: 700,
              fontSize: 34,
              letterSpacing: '-.01em',
              lineHeight: 1.05,
            }}
          >
            {on && domKey ? PL_LABEL[domKey] : '—'}
          </Typography>
          {on && dom && (
            <Typography
              sx={{ fontWeight: 700, fontSize: 18, fontVariantNumeric: 'tabular-nums' }}
            >
              {Math.round(dom[1] * 100)}%
            </Typography>
          )}
        </Box>
        {!on && (
          <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
            Brak danych w tym momencie
          </Typography>
        )}
      </Box>

      {/* Distribution bars */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {FA_ORDER.map((k) => {
          const color = FA_COLOR[k]
          const v = vec ? vec[k] ?? 0 : 0
          const pct = Math.max(0, Math.min(100, v * 100))
          const isDom = on && k === domKey
          return (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography
                component="span"
                sx={{
                  fontSize: 12.5,
                  fontWeight: isDom ? 700 : 400,
                  color: c.ink,
                  opacity: on && !isDom ? 0.6 : 1,
                  flex: '0 0 104px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{ width: 10, height: 10, borderRadius: '3px', background: color }}
                />
                {PL_LABEL[k]}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 20,
                  background: c.gray100,
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    height: '100%',
                    background: color,
                    opacity: on && !isDom ? 0.85 : 1,
                    borderRadius: '6px',
                    transition: 'width 180ms cubic-bezier(.2,0,0,1)',
                  }}
                />
              </Box>
              <Typography
                component="span"
                sx={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: isDom ? color : c.ink56,
                  fontVariantNumeric: 'tabular-nums',
                  flex: '0 0 44px',
                  textAlign: 'right',
                }}
              >
                {Math.round(pct)}%
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
